import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BrandMark } from '../../components/brand/BrandMark';
import { useSession } from '../../lib/session';
import { useTheme } from '../../lib/theme';
import { Ic } from './Icon';
import type { LandingContent } from './content';

interface Props {
  c: LandingContent;
  onOpenDemo: () => void;
}

const LINKS = ['features', 'live', 'how', 'industries', 'faq'] as const;

export function Navbar({ c, onOpenDemo }: Props) {
  const { i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const { status, realm } = useSession();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isAr = i18n.language !== 'en';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const toggleLang = () => void i18n.changeLanguage(isAr ? 'en' : 'ar');
  const signedIn = status === 'authenticated';
  const appHome = realm === 'super' ? '/admin' : '/';

  return (
    <>
      <header className={`nav${scrolled ? ' scrolled' : ''}`}>
        <div className="lp-container">
          <div className="nav-inner">
            <a className="brand" href="#top" aria-label="RFleet">
              <BrandMark size={38} />
              <span className="brand-txt">
                <span>
                  RFleet<span className="ver">2.0</span>
                </span>
                <small>{c.nav.tagline}</small>
              </span>
            </a>
            <nav className="nav-links" aria-label="Primary">
              {LINKS.map((k) => (
                <a key={k} href={`#${k}`}>
                  {c.nav[k]}
                </a>
              ))}
            </nav>
            <div className="nav-actions">
              <button className="icon-btn lang-btn" type="button" onClick={toggleLang} aria-label={isAr ? 'Switch to English' : 'التبديل إلى العربية'}>
                <Ic name="globe" />
                <span>{isAr ? 'EN' : 'ع'}</span>
              </button>
              <button className="icon-btn" type="button" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}>
                <Ic name={theme === 'dark' ? 'sun' : 'moon'} />
              </button>
              <Link className="lp-btn lp-btn-ghost nav-login" to={signedIn ? appHome : '/login'}>
                {signedIn ? c.nav.dashboard : c.nav.login}
              </Link>
              <button type="button" className="lp-btn lp-btn-primary nav-demo" onClick={onOpenDemo}>
                {c.nav.demo}
              </button>
              <button className="icon-btn menu-btn" type="button" onClick={() => setMenuOpen(true)} aria-label="Open menu" aria-expanded={menuOpen}>
                <Ic name="menu" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className={`mobile-menu${menuOpen ? ' open' : ''}`} aria-hidden={!menuOpen}>
        <button className="icon-btn close" type="button" onClick={() => setMenuOpen(false)} aria-label="Close menu" tabIndex={menuOpen ? 0 : -1}>
          <Ic name="x" />
        </button>
        {LINKS.map((k) => (
          <a key={k} href={`#${k}`} onClick={() => setMenuOpen(false)} tabIndex={menuOpen ? 0 : -1}>
            {c.nav[k]}
          </a>
        ))}
        <Link to={signedIn ? appHome : '/login'} onClick={() => setMenuOpen(false)} tabIndex={menuOpen ? 0 : -1}>
          {signedIn ? c.nav.dashboard : c.nav.login}
        </Link>
        <a
          href="#contact"
          style={{ color: 'var(--brand-ink)' }}
          tabIndex={menuOpen ? 0 : -1}
          onClick={(e) => {
            e.preventDefault();
            setMenuOpen(false);
            onOpenDemo();
          }}
        >
          {c.nav.demo}
        </a>
      </div>
    </>
  );
}
